import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";
import styled from "styled-components";
import Loading from "components/Loading/Loading";
import { isTrackCodeInternalUser } from "common/trackcodeInternal";
import CollaborationIcon from "./components/CollaborationIcon";
import CollaborationHeader, {
  navigateCollaborationAfterTenantSwitch
} from "./components/CollaborationHeader";
import { fetchArtifact, fetchWorkspace } from "./CollaborationService";
import { resolveArtifactBodyHtml } from "./artifactBodyUtils";
import { resolveArtifactRenderer } from "./renderers";
import {
  formatArtifactDate,
  formatArtifactStatus,
  formatArtifactType,
  formatCollaborationHeaderLabel,
  formatCollaborationHeaderTitle,
  formatRelativeTime,
  getOwnerProfile
} from "./collaborationUtils";
import {
  COLLABORATION_INDEX_PATH,
  didCollaborationTenantContextChange,
  shouldFetchCollaborationData
} from "./collaborationTenantChange";

class CollaborationArtifactDetail extends Component {
  static propTypes = {
    match: PropTypes.shape({
      params: PropTypes.shape({
        slug: PropTypes.string.isRequired,
        key: PropTypes.string.isRequired
      }).isRequired
    }).isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
      replace: PropTypes.func.isRequired
    }).isRequired,
    user: PropTypes.object,
    logisticCompanyId: PropTypes.number,
    logisticCompanyPrepareChange: PropTypes.bool,
    managedLogisticCompanies: PropTypes.object
  };

  state = {
    workspace: null,
    artifact: null,
    loading: true,
    error: null
  };

  componentDidMount() {
    if (shouldFetchCollaborationData(this.props)) {
      this.loadArtifact();
    }
  }

  componentDidUpdate(prevProps) {
    if (didCollaborationTenantContextChange(prevProps, this.props)) {
      if (shouldFetchCollaborationData(this.props)) {
        this.props.history.replace(COLLABORATION_INDEX_PATH);
      }
      return;
    }

    const prevParams = prevProps.match.params;
    const nextParams = this.props.match.params;

    if (
      prevParams.slug !== nextParams.slug ||
      prevParams.key !== nextParams.key
    ) {
      this.loadArtifact();
    }
  }

  loadArtifact = async () => {
    const { slug, key } = this.props.match.params;
    this.setState({
      loading: true,
      error: null,
      workspace: null,
      artifact: null
    });

    try {
      const [workspace, artifact] = await Promise.all([
        fetchWorkspace(slug),
        fetchArtifact(slug, key)
      ]);
      this.setState({ workspace, artifact, loading: false });
    } catch (error) {
      const status = error?.response?.status;
      this.setState({
        loading: false,
        error:
          status === 404
            ? "Artefakt nicht gefunden."
            : "Artefakt konnte nicht geladen werden."
      });
    }
  };

  render() {
    const { workspace, artifact, loading, error } = this.state;
    const {
      match: {
        params: { slug }
      },
      user,
      logisticCompanyId,
      managedLogisticCompanies
    } = this.props;
    const headerLabel = formatCollaborationHeaderLabel({
      managedLogisticCompanies,
      logisticCompanyId,
      user,
      partner: workspace?.partner
    });
    const { customerName } = formatCollaborationHeaderTitle({
      managedLogisticCompanies,
      logisticCompanyId,
      user,
      partner: workspace?.partner
    });

    if (loading) {
      return (
        <Page>
          <Loading />
        </Page>
      );
    }

    if (error || !workspace || !artifact) {
      return (
        <Page>
          <CollaborationHeader onAfterSwitch={navigateCollaborationAfterTenantSwitch} />
          <ContentBody>
            <EmptyState>
              <EmptyTitle>{error || "Artefakt nicht gefunden."}</EmptyTitle>
              <BackLink to={`/collaboration/${slug}`}>
                <CollaborationIcon name="arrow-left" size={14} />
                Zurück zum Workspace
              </BackLink>
            </EmptyState>
          </ContentBody>
        </Page>
      );
    }

    const owner = getOwnerProfile(artifact.owner);
    const relativeTime = formatRelativeTime(artifact.publishedAt);
    const bodyHtml = resolveArtifactBodyHtml({
      bodyHtml: artifact.bodyHtml,
      bodyMd: artifact.bodyMd
    });
    const ArtifactRenderer = resolveArtifactRenderer({
      type: artifact.type,
      contentFormat: artifact.contentFormat
    });
    const hasBlockContent =
      ArtifactRenderer && artifact.contentFormat === "blocks" && artifact.blocks;
    const isInternalUser = isTrackCodeInternalUser(user);

    return (
      <Page data-collaboration-page $blockView={hasBlockContent}>
        <CollaborationHeader
          partner={workspace.partner}
          onAfterSwitch={navigateCollaborationAfterTenantSwitch}
        />

        <ContentBody $fullWidth={hasBlockContent} data-collaboration-scroll>
          {hasBlockContent ? (
            <ArtifactRenderer
              blocks={artifact.blocks}
              artifact={artifact}
              workspace={workspace}
              isInternalUser={isInternalUser}
              backLabel={customerName}
              onBack={() => this.props.history.push(`/collaboration/${slug}`)}
            />
          ) : (
            <>
            <BackLink to={`/collaboration/${slug}`}>
              <CollaborationIcon name="arrow-left" size={14} />
              Zurück zu {headerLabel}
            </BackLink>
            <Article>
              <TopRow>
                <TypeBadge>{formatArtifactType(artifact.type)}</TypeBadge>
                <StatusPill>{formatArtifactStatus(artifact.status)}</StatusPill>
                <ContextLabel>
                  <CollaborationIcon name="calendar" size={12} />
                  {formatArtifactDate(artifact.date)}
                </ContextLabel>
                {artifact.location && (
                  <ContextLabel>
                    <CollaborationIcon name="map-pin" size={12} />
                    {artifact.location}
                  </ContextLabel>
                )}
              </TopRow>

              <Title>{artifact.title}</Title>

              <MetaRow>
                <MetaAvatar>{owner.initials}</MetaAvatar>
                <span>{owner.fullName}</span>
                {relativeTime && (
                  <>
                    <Dot>·</Dot>
                    <span>{relativeTime}</span>
                  </>
                )}
              </MetaRow>

              {artifact.lead && <Lead>{artifact.lead}</Lead>}

              {bodyHtml && (
                <>
                  <Divider />
                  <Body dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                </>
              )}

              {artifact.nextStep?.what && (
                <NextStep>
                  <NextStepIcon>
                    <CollaborationIcon name="arrow-right" size={16} />
                  </NextStepIcon>
                  <NextStepBody>
                    <NextStepLabel>Nächster Schritt</NextStepLabel>
                    <NextStepWhat>{artifact.nextStep.what}</NextStepWhat>
                    {artifact.nextStep.detail && (
                      <NextStepDetail>{artifact.nextStep.detail}</NextStepDetail>
                    )}
                  </NextStepBody>
                </NextStep>
              )}
            </Article>
            </>
          )}
        </ContentBody>
      </Page>
    );
  }
}

const mapStateToProps = state => ({
  user: state.auth?.user,
  logisticCompanyId: state.auth?.logisticCompanyId,
  logisticCompanyPrepareChange: state.auth?.logisticCompanyPrepareChange,
  managedLogisticCompanies: state.auth?.managedLogisticCompanies
});

export default withRouter(connect(mapStateToProps)(CollaborationArtifactDetail));

const Page = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: ${props => (props.$blockView ? "#f4f3ef" : "#f7f6f2")};
  overflow: hidden;
`;

const ContentBody = styled.div`
  flex: 1;
  min-height: 0;
  padding: ${props => (props.$fullWidth ? "0" : "24px 32px 32px")};
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  font-size: 13px;
  font-weight: 500;
  color: #0a7c4e;
  text-decoration: none;

  &:hover {
    color: #065f3a;
    text-decoration: underline;
  }
`;

const Article = styled.article`
  max-width: 760px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 28px 32px 32px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const TypeBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 999px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: #e6f5ef;
  color: #0a7c4e;
`;

const StatusPill = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
`;

const ContextLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 500;
  padding: 3px 9px;
  border-radius: 999px;
  background: #f0eee8;
  color: #6a6960;

  svg {
    opacity: 0.7;
  }
`;

const Title = styled.h1`
  margin: 0 0 10px;
  font-size: 28px;
  font-weight: 700;
  color: #181714;
  line-height: 1.25;
  letter-spacing: -0.01em;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6a6960;
  margin-bottom: 16px;
`;

const MetaAvatar = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #e2dfd8;
  color: #5f5e5a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  flex-shrink: 0;
`;

const Dot = styled.span`
  color: #c8c5bc;
`;

const Lead = styled.p`
  margin: 0 0 20px;
  font-size: 15px;
  color: #2c2b27;
  line-height: 1.55;
`;

const Divider = styled.hr`
  margin: 0 0 24px;
  border: none;
  border-top: 1px solid #f0eee8;
`;

const Body = styled.div`
  font-size: 15px;
  line-height: 1.65;
  color: #2c2b27;

  h1,
  h2,
  h3,
  h4 {
    margin: 1.4em 0 0.6em;
    color: #181714;
    line-height: 1.3;
    letter-spacing: -0.005em;
  }

  h1 {
    font-size: 24px;
  }

  h2 {
    font-size: 20px;
  }

  h3 {
    font-size: 17px;
  }

  p {
    margin: 0 0 1em;
  }

  ul,
  ol {
    margin: 0 0 1em;
    padding-left: 1.4em;
  }

  li {
    margin-bottom: 0.35em;
  }

  a {
    color: #0a7c4e;
    text-decoration: underline;
  }

  blockquote {
    margin: 0 0 1em;
    padding-left: 16px;
    border-left: 3px solid #e2dfd8;
    color: #6a6960;
  }

  strong {
    font-weight: 600;
    color: #181714;
  }
`;

const NextStep = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 28px;
  padding: 12px 14px;
  background: #faf9f5;
  border-radius: 6px;
  font-size: 13px;
  color: #2c2b27;
`;

const NextStepIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e6f5ef;
  color: #0a7c4e;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const NextStepBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const NextStepLabel = styled.div`
  font-size: 10px;
  color: #a09e96;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  margin-bottom: 2px;
`;

const NextStepWhat = styled.div`
  font-weight: 600;
`;

const NextStepDetail = styled.div`
  font-size: 12px;
  color: #6a6960;
  margin-top: 1px;
`;

const EmptyState = styled.section`
  max-width: 520px;
  margin: 48px auto 0;
  text-align: center;
`;

const EmptyTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 20px;
  font-weight: 700;
  color: #181714;
`;
